import Add from "@mui/icons-material/Add";
import Remove from "@mui/icons-material/Remove";
import { Box, Button, IconButton, Stack, SxProps, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { CheckboxField } from "components/Forms/CheckboxField";
import { DateField } from "components/Forms/DateField";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { FormTextField } from "components/Forms/FormTextField";
import { SelectWithFreeSolo } from "components/Forms/SelectWithFreeSolo";
import { alternateIdTypes } from "encamp-shared/src/constants/alternateIdTypes";
import { useCallback } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { v4 as uuidV4 } from "uuid";

export type FormShapeWithAlternateIds = {
  facilityAlternateIds: {
    id?: string;
    type?: string | null;
    value?: string | null;
    hasNoId?: boolean | null;
    expiresAt?: string | Date | null;
  }[];
};

const gridSx: SxProps = {
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  columnGap: 2,
  rowGap: 0,
  "& .MuiFormHelperText-root.Mui-error": {
    mb: 1,
  },
};

export function GovernmentIdFields({ isEpaIdLocked }: { isEpaIdLocked: boolean }) {
  const { control, formState } = useFormContext<FormShapeWithAlternateIds>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "facilityAlternateIds",
  });
  const fieldState = control.getFieldState("facilityAlternateIds");
  const watchedFields = useWatch({
    control,
    name: "facilityAlternateIds",
  });

  const pastExpiresAt = useCallback((date: Date | string | undefined | null) => {
    if (!date) return false;
    return new Date(date) < new Date();
  }, []);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" fontWeight={600} mb={3}>
          Government IDs
        </Typography>
        <IconButton
          size="small"
          color="primary"
          onClick={() =>
            append({
              id: uuidV4(),
              type: "",
              value: "",
              hasNoId: false,
            })
          }
        >
          <Add />
        </IconButton>
      </Stack>
      {fields.length === 0 && (
        <Button
          startIcon={<Add />}
          onClick={() =>
            append({
              id: uuidV4(),
              type: "",
              value: "",
              hasNoId: false,
            })
          }
          variant="text"
          sx={{ alignSelf: "flex-start" }}
        >
          ADD ID
        </Button>
      )}
      <Stack spacing={2}>
        {fields.map((alternateId, index) => {
          const hasNoId = watchedFields?.[index]?.hasNoId ?? false;
          const isEpa = watchedFields?.[index]?.type === "EPA";
          const isLockedEpa = isEpa && isEpaIdLocked;
          return (
            <Stack key={alternateId.id} direction="row" alignItems="start" spacing={0}>
              <Box sx={{ ...gridSx, flex: 1 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <SelectWithFreeSolo
                    name={`facilityAlternateIds.${index}.type`}
                    control={control}
                    options={alternateIdTypes}
                    disabled={isLockedEpa}
                    label="ID Type *"
                  />
                  <ErrorDisplay
                    error={
                      control.getFieldState(`facilityAlternateIds.${index}.type`, formState).error
                    }
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}>
                  {hasNoId ? (
                    <>
                      <TextField sx={{ display: "flex" }} label="ID" disabled />
                      <ErrorDisplay />
                    </>
                  ) : (
                    <FormTextField
                      label="ID *"
                      control={control}
                      name={`facilityAlternateIds.${index}.value`}
                      disabled={isLockedEpa || pastExpiresAt(watchedFields?.[index]?.expiresAt)}
                    />
                  )}
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}>
                  {hasNoId ? (
                    <DatePicker label="Expiration Date" disabled />
                  ) : (
                    <DateField
                      name={`facilityAlternateIds.${index}.expiresAt`}
                      label="Expiration Date"
                      control={control}
                      disabled={isLockedEpa}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    gridColumn: { xs: "span 12", sm: "span 2" },
                    pt: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  <CheckboxField
                    name={`facilityAlternateIds.${index}.hasNoId`}
                    label="No ID"
                    control={control}
                    disabled={isLockedEpa}
                    noErrorDisplay
                  />
                </Box>
              </Box>
              {!isLockedEpa && (
                <Box sx={{ pt: 1 }}>
                  <IconButton size="small" onClick={() => remove(index)} color="default">
                    <Remove />
                  </IconButton>
                </Box>
              )}
            </Stack>
          );
        })}
      </Stack>
      {fieldState.error && <ErrorDisplay error={fieldState.error} />}
    </Box>
  );
}
